import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Snackbar,
  Alert,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import RestoreIcon from '@mui/icons-material/Restore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { edgeAccountDigestsApi } from '../../api/services';
import { extractApiError, extractApiErrorInfo } from '../../utils/apiErrors';
import { formatTimestampAge, freshnessColor } from '../../utils/formatTimestampAge';

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  digestId: number | null;
}

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  open,
  onClose,
  digestId,
}) => {
  const [tabValue, setTabValue] = React.useState(0);
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = React.useState(false);
  // Warning modal when approve is refused by an overridable lock (outage/silent),
  // mirroring the reports page so this second approve entry point isn't a
  // dead-end during an outage (sc-6006). Approve-only here (no send action).
  const [overridePrompt, setOverridePrompt] = React.useState<{ message: string } | null>(null);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!open) {
      setApproveDialogOpen(false);
      setCancelDialogOpen(false);
      setRestoreDialogOpen(false);
      setOverridePrompt(null);
    }
  }, [open]);

  const { data: digest, isLoading, isError } = useQuery({
    queryKey: ['edge_account_digest', digestId],
    queryFn: () => edgeAccountDigestsApi.getOne(digestId!),
    enabled: open && digestId !== null,
  });

  const approveMutation = useMutation({
    mutationFn: (override?: boolean) => edgeAccountDigestsApi.approve(digestId!, override),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      queryClient.invalidateQueries({ queryKey: ['edge_account_digest', digestId] });
      setSnackbar({ message: 'Digest approved successfully', severity: 'success' });
      setApproveDialogOpen(false);
      setOverridePrompt(null);
    },
    onError: (error) => {
      const info = extractApiErrorInfo(error, 'Failed to approve digest');
      if (info.overridable) {
        setApproveDialogOpen(false);
        setOverridePrompt({ message: info.message });
      } else {
        setSnackbar({ message: info.message, severity: 'error' });
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => edgeAccountDigestsApi.cancel(digestId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      queryClient.invalidateQueries({ queryKey: ['edge_account_digest', digestId] });
      setSnackbar({ message: 'Auto-send cancelled', severity: 'success' });
      setCancelDialogOpen(false);
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to cancel auto-send'), severity: 'error' });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => edgeAccountDigestsApi.restore(digestId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      queryClient.invalidateQueries({ queryKey: ['edge_account_digest', digestId] });
      setSnackbar({ message: 'Auto-send restored', severity: 'success' });
      setRestoreDialogOpen(false);
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to restore auto-send'), severity: 'error' });
    },
  });

  const handleCopy = async () => {
    if (!digest) return;

    try {
      if (tabValue === 0) {
        // For HTML: Copy as rich HTML that email clients can interpret
        const htmlContent = digest.email_html_body || '';
        if (!htmlContent) {
          setSnackbar({ message: 'No HTML content available to copy', severity: 'error' });
          return;
        }

        // Use Clipboard API with HTML format
        // Create a plain text fallback by stripping HTML tags
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';

        // Use ClipboardItem to copy both HTML and plain text
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });

        await navigator.clipboard.write([clipboardItem]);
        setSnackbar({
          message: 'Copied HTML content to clipboard',
          severity: 'success'
        });
      } else {
        // For Text: Copy as plain text
        const textContent = digest.email_text_body || '';
        if (!textContent) {
          setSnackbar({ message: 'No text content available to copy', severity: 'error' });
          return;
        }

        await navigator.clipboard.writeText(textContent);
        setSnackbar({
          message: 'Copied text content to clipboard',
          severity: 'success'
        });
      }
    } catch (err) {
      // Fallback to execCommand if Clipboard API fails (for older browsers)
      if (tabValue === 0) {
        const htmlContent = digest.email_html_body || '';
        if (!htmlContent) {
          setSnackbar({ message: 'No HTML content available to copy', severity: 'error' });
          return;
        }

        // Fallback: Create temporary element and use execCommand
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        document.body.appendChild(tempDiv);

        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        const successful = document.execCommand('copy');
        selection?.removeAllRanges();
        document.body.removeChild(tempDiv);

        if (successful) {
          setSnackbar({
            message: 'Copied HTML content to clipboard',
            severity: 'success'
          });
        } else {
          setSnackbar({ message: 'Failed to copy to clipboard', severity: 'error' });
        }
      } else {
        setSnackbar({ message: 'Failed to copy to clipboard', severity: 'error' });
      }
    }
  };

  if (!digestId) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Email Preview: {digest?.location?.name || 'Unknown Location'}
        <Tooltip title={digest?.hexahq_quote_url ? '' : 'No quote exists for this digest'}>
          <span>
            <Button
              startIcon={<OpenInNewIcon />}
              variant="outlined"
              size="small"
              disabled={!digest?.hexahq_quote_url}
              {...(digest?.hexahq_quote_url ? {
                href: digest.hexahq_quote_url,
                target: '_blank',
                rel: 'noopener noreferrer',
              } : {})}
            >
              View Quote in HexaHQ
            </Button>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        {isError ? (
          <Alert severity="error">Failed to load digest preview. Close and try again.</Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {digest?.status === 'draft' && digest?.auto_send_failure_reason && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Auto-send failed
                {digest.auto_send_attempted_at
                  ? ` at ${new Date(digest.auto_send_attempted_at).toLocaleString()}`
                  : ''}
                : {digest.auto_send_failure_reason}
                {digest.auto_send_failure_message
                  ? ` — ${digest.auto_send_failure_message}`
                  : ''}
                . Needs manual approval.
              </Alert>
            )}
            {digest?.status === 'draft'
              && digest?.scheduled_send_at
              && !digest?.auto_send_failure_reason && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Scheduled to auto-send at{' '}
                {new Date(digest.scheduled_send_at).toLocaleString()}. When the
                send time arrives, the system will refresh inventory data,
                create a HexaHQ quote, and email this digest. Click{' '}
                <strong>Cancel Auto-Send</strong> to prevent it.
              </Alert>
            )}
            {digest?.status === 'draft' && digest?.refresh_status === 'skipped_outage' && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Data was not refreshed — a covering device is in an active outage.
                Inventory numbers below reflect the most recent successful refresh.
                Most outages clear within ~30 min; approval will resume once the
                device reports in.
              </Alert>
            )}
            {digest?.status === 'draft' && digest?.inventory_data_timestamp && (() => {
              const { timeStr, agoStr, freshness } = formatTimestampAge(digest.inventory_data_timestamp);
              return (
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: freshnessColor(freshness) }}
                >
                  Last scan at: {timeStr}, {agoStr}
                </Typography>
              );
            })()}
            {digest?.status !== 'draft' && digest?.approved_at && (() => {
              const { timeStr, agoStr } = formatTimestampAge(digest.approved_at);
              return (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Approved{digest.approved_by ? ` by ${digest.approved_by}` : ''} at {timeStr}, {agoStr}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    Quantities shown in this digest are locked at approval time and do not update.
                  </Typography>
                </Box>
              );
            })()}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                <Tab label="HTML" />
                <Tab label="Text" />
              </Tabs>
            </Box>

            {tabValue === 0 ? (
              <iframe
                srcDoc={digest?.email_html_body || ''}
                title="Email Preview"
                style={{
                  width: '100%',
                  height: '60vh',
                  border: 'none',
                }}
                sandbox=""
              />
            ) : (
              <Box
                sx={{ maxHeight: '60vh', overflow: 'auto' }}
              >
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                  }}
                >
                  {digest?.email_text_body || 'No text content available'}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {/*
          Auto-send-scheduled drafts (status='draft' + scheduled_send_at set +
          no recorded failure) get only the Cancel Auto-Send action — the
          send-time job will run the full approve pipeline on its own.
          Showing Approve alongside would invite a manual approve that
          races the scheduled job. A recorded failure flips the digest back
          into the manual queue, so Approve returns.
        */}
        {(() => {
          const isAutoSendScheduled =
            digest?.status === 'draft'
            && !!digest?.scheduled_send_at
            && !digest?.auto_send_failure_reason;

          const isRestorable =
            digest?.status === 'cancelled'
            && !!digest?.scheduled_send_at
            && new Date(digest.scheduled_send_at).getTime() > Date.now();

          if (isAutoSendScheduled) {
            return (
              <Button
                startIcon={<CancelScheduleSendIcon />}
                onClick={() => setCancelDialogOpen(true)}
                color="warning"
                variant="outlined"
                disabled={cancelMutation.isPending}
              >
                Cancel Auto-Send
              </Button>
            );
          }

          if (isRestorable) {
            return (
              <Button
                startIcon={<RestoreIcon />}
                onClick={() => setRestoreDialogOpen(true)}
                color="primary"
                variant="contained"
                disabled={restoreMutation.isPending}
              >
                Restore Auto-Send
              </Button>
            );
          }

          return (
            <Tooltip title={digest?.status && digest.status !== 'draft' ? 'Digest has already been approved' : ''}>
              <span>
                <Button
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setApproveDialogOpen(true)}
                  variant="contained"
                  disabled={!digest || digest.status !== 'draft' || approveMutation.isPending}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
          );
        })()}
        <Button
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          variant="outlined"
          disabled={isLoading || isError || !digest}
        >
          Copy {tabValue === 0 ? 'HTML' : 'Text'}
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve Digest</DialogTitle>
        <DialogContent>
          <Typography>
            {digest?.restock_data && digest.restock_data.length > 0
              ? 'Approve this digest and create the HexaHQ quote? Quantities will be locked.'
              : 'Approve this digest? Quantities will be locked.'}
          </Typography>
          {digest?.scheduled_send_at && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
              Approving now supersedes the scheduled auto-send at{' '}
              {new Date(digest.scheduled_send_at).toLocaleString()}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => approveMutation.mutate(undefined)}
            variant="contained"
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending
              ? (digest?.restock_data?.length ? 'Creating quote...' : 'Approving...')
              : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override warning modal (sc-6006): force past an outage/silent lock. */}
      <Dialog open={!!overridePrompt} onClose={() => setOverridePrompt(null)}>
        <DialogTitle>Override approval lock?</DialogTitle>
        <DialogContent>
          {overridePrompt?.message && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {overridePrompt.message}
            </Alert>
          )}
          <Typography>
            This bypasses a safety check and will send possibly-stale inventory
            data to the customer. Approve anyway?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverridePrompt(null)}>Cancel</Button>
          <Button
            onClick={() => approveMutation.mutate(true)}
            variant="contained"
            color="warning"
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? 'Approving...' : 'Approve Anyway'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Auto-Send</DialogTitle>
        <DialogContent>
          <Typography>
            Cancel the scheduled auto-send for this digest? The digest will be
            marked cancelled and will not be sent to the customer.
          </Typography>
          {digest?.scheduled_send_at && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Was scheduled for {new Date(digest.scheduled_send_at).toLocaleString()}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Scheduled</Button>
          <Button
            onClick={() => cancelMutation.mutate()}
            variant="contained"
            color="warning"
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Auto-Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>Restore Auto-Send</DialogTitle>
        <DialogContent>
          <Typography>
            Restore this digest to the auto-send queue? The customer will receive
            the digest at the originally-scheduled time. The cancel record will
            be cleared.
          </Typography>
          {digest?.scheduled_send_at && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Will send at {new Date(digest.scheduled_send_at).toLocaleString()}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Keep Cancelled</Button>
          <Button
            onClick={() => restoreMutation.mutate()}
            variant="contained"
            color="primary"
            disabled={restoreMutation.isPending}
          >
            {restoreMutation.isPending ? 'Restoring...' : 'Restore Auto-Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar?.severity}
          onClose={() => setSnackbar(null)}
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
